import iamService from '@/services/iamService'
import { TranslationFactory } from '@/translators/TranslationFactory'
import { makeObjectsUniqueWithMap } from '@/helpers/objectHelpers'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { isPermissionsListType, PermissionType } from '@/types/permissions/PermissionType'
import { devDebug } from '@/helpers/errorHelper'

export const CustomerPermissionsStore = defineStore(
  'customerPermissions',
  () => {
    const customerPermissions = ref<Array<PermissionType>>([])
    const selectedCustomerId = ref<string | undefined>(undefined)
    const selectedRelationContactId = ref<string | undefined>(undefined)
    const selectedSource = ref<string | undefined>(undefined)
    const isError = ref<boolean>(false)
    const permissionsLoaded = ref<boolean>(false)
    // cache permissions
    const lastUpdated = ref<number | null>(null) // Timestamp in ms
    const REFRESH_INTERVAL = 5 * 60 * 1000 // 5 minutes

    /**
     * Internal lock to prevent multiple simultaneous API calls.
     * If a request is in flight, all subsequent callers will await the same promise.
     */
    let activeRefreshPromise: Promise<boolean> | null = null

    async function refresh(force = false): Promise<boolean> {
      const now = Date.now()

      // Determine if data is "stale"
      const isStale = !lastUpdated.value || now - lastUpdated.value > REFRESH_INTERVAL

      //  Return active promise if one is already running
      if (activeRefreshPromise) return activeRefreshPromise

      //  If not forcing AND data isn't stale AND we have data -> return success
      if (!force && !isStale && customerPermissions.value.length > 0) {
        return true
      } else if (force) {
        // Clear cached permissions immediately on new session to prevent stale data
        customerPermissions.value = []
      } else {
        devDebug(
          `Refreshing session, force: ${force} isStale: ${isStale}, permissions: ${customerPermissions.value.length}`,
        )
      }

      // ASSIGN THE PROMISE IMMEDIATELY
      activeRefreshPromise = (async () => {
        try {
          const response = await iamService.getUserPermissions()
          const translator = TranslationFactory.getResponseTranslator(response)
          const translatedResponse = translator.convert(response)

          if (isPermissionsListType(translatedResponse)) {
            const customers = translatedResponse.filter((x) => x.type === 'company')
            const uniqueCustomers = makeObjectsUniqueWithMap(customers, (obj) => obj.id)

            customerPermissions.value = uniqueCustomers.map((permission) => {
              if (permission.system === 'mind') {
                permission.privileges = ['InvoiceMindManagement']
              }
              return permission
            })

            isError.value = false
            permissionsLoaded.value = true
            // update cache timestamp
            lastUpdated.value = Date.now()
            return true
          }

          isError.value = true
          return false
        } catch (error) {
          console.error('Failed to refresh customer permissions:', error)
          isError.value = true
          return false
        } finally {
          setTimeout(() => {
            activeRefreshPromise = null
          }, 500) // 0.5 second delay to decrease race cond chance
        }
      })()

      // resolve IMMEDIATELY but let the promise above continue in the background.
      if (!force && customerPermissions.value.length > 0) {
        return Promise.resolve(true)
      }

      return activeRefreshPromise
    }

    function setIsError(error: boolean): void {
      isError.value = error
    }

    function selectCustomerId(customerId: string): void {
      selectedCustomerId.value = customerId
    }

    function setSource(source: string): void {
      selectedSource.value = source
    }

    function selectRelationContactId(relationContactId: string): void {
      selectedRelationContactId.value = relationContactId
    }

    function getCustomerPermission(customerId: string | undefined): PermissionType | undefined {
      if (!customerId) {
        return undefined
      }
      return customerPermissions.value.find((x) => x.id == customerId)
    }

    function clearStore(): void {
      customerPermissions.value = []
      isError.value = false
      selectedCustomerId.value = undefined
      selectedRelationContactId.value = undefined
      selectedSource.value = undefined
      permissionsLoaded.value = false
      lastUpdated.value = null
    }

    return {
      isError,
      customerPermissions,
      permissionsLoaded,
      selectedCustomerId,
      selectedRelationContactId,
      selectedSource,
      lastUpdated,
      setSource,
      selectCustomerId,
      selectRelationContactId,
      getCustomerPermission,
      refresh,
      setIsError,
      clearStore,
    }
  },
  {
    persist: {
      storage: localStorage,
    },
  },
)
